import { calculateDayDifference, dateFormatDialog, generateCountString } from '@alluradirect/react';
import Button from '@mui/material/Button';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';
import {
  SelectedDateRange,
  useDateRangePicker,
} from 'components/views/pages/Checkout/CheckoutDateRangePicker/useDateRangePicker';
import { DateRangePickerDay } from 'components/views/pages/Checkout/CheckoutDateRangePicker/components/CheckoutDateRangePickerDay';
import { CheckoutDateRangePickerFooter } from 'components/views/pages/Checkout/CheckoutDateRangePicker/components/CheckoutDateRangePickerFooter';
import { DateRangePickerNavbar } from 'components/views/pages/Checkout/CheckoutDateRangePicker/components/CheckoutDateRangePickerNavbar';
import { CheckoutDateRangePickerYearMonthSelect } from 'components/views/pages/Checkout/CheckoutDateRangePicker/components/CheckoutDateRangePickerYearMonthSelect';
import { StyledDayPicker, DatePickerContent } from 'components/views/pages/Checkout/CheckoutDateRangePicker/styles';
import { UnavailableDates } from 'components/views/pages/Checkout/hooks/usePropertyUnavilableDates';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { Fade } from '@mui/material';


const NUM_MONTHS = 2;

interface Props {
  isOpen: boolean;
  startDate: string | undefined;
  endDate: string | undefined;
  lastDateOfRates: string;
  lastBookableDate: string;
  minNightsDates: UnavailableDates['MIN_NIGHTS_DATES'];
  unavailabilityArrival: string[];
  unavailabilityDeparture: string[];
  cannotArrive: string[];
  cannotDepart: string[];
  close: () => void;
  onSaveDates: (dateRange: SelectedDateRange) => void;
  ratesData?: { [date: string]: [rate: string] };
}

export const CheckoutDateRangePicker = ({
  startDate,
  endDate,
  isOpen,
  minNightsDates,
  lastDateOfRates,
  lastBookableDate,
  unavailabilityArrival,
  unavailabilityDeparture,
  cannotArrive,
  cannotDepart,
  close,
  onSaveDates,
  ratesData,
}: Props) => {
  const { isXs } = useScreenWidth();
  const [loadMoreDatesDisabled, setLoadMoreDatesDisabled] = useState(false);

  const {
    modifiers,
    selectedDays,
    disabledDays: disabledDaysTotal,
    selectedDateRange,
    handleDayClick,
    resetState,
    currMonth,
    currYear,
    setCurrMonth,
    setCurrYear,
    month,
    numMonths,
    setNumMonths,
  } = useDateRangePicker({
    initialStart: startDate,
    initialEnd: endDate,
    minNightsDates,
    lastDateOfRates,
    lastBookableDate,
    unavailabilityArrival,
    unavailabilityDeparture,
    cannotArrive,
    cannotDepart,
  });

  const dialogTitle = useMemo(() => {
    if (!selectedDateRange.to || !selectedDateRange.from) return 'Select Dates to Book';
    const nights = Math.abs(calculateDayDifference(selectedDateRange.to, selectedDateRange.from));
    const start = dateFormatDialog(selectedDateRange.from);
    const end = dateFormatDialog(selectedDateRange.to);

    return `${start} - ${end} (${generateCountString(nights, 'Nt')})`;
  }, [selectedDateRange.from, selectedDateRange.to]);

  const clearDates = () => {
    resetState();
  };

  const onMonthChange = (month: Date) => {
    const date = dayjs(month);
    setCurrYear(date.year());
    setCurrMonth(date.month() + 1);
  };

  const onTodayClick = () => {
    onMonthChange(new Date());
  };

  const increaseNumMonths = () => {
    setNumMonths(m => m + NUM_MONTHS);
  };

  const toMonth = useMemo(() => {
    const lastValidCheckout = dayjs(lastDateOfRates).add(1, 'day');
    // Fixes issue where 'next' button is disabled when lastMonth is odd
    if ((lastValidCheckout.month() + 1) % 2 !== 0) {
      return lastValidCheckout.add(1, 'month').toDate();
    }
    return lastValidCheckout.toDate();
  }, [lastDateOfRates]);

  const calRef = useRef<DayPicker | null>(null);
  const showNextMonth = () => {
    if (!calRef.current) return;

    const daypicker = calRef.current.dayPicker;
    const monthCaptions = daypicker.querySelectorAll('.DayPicker-Caption');

    let disabled = false;
    monthCaptions.forEach(caption => {
      const monthCaption = caption.children[0].innerHTML;
      const date = dayjs(monthCaption);
      const lastDate = dayjs(lastDateOfRates);
      if (date.year() === lastDate.year() && date.month() === lastDate.month()) {
        disabled = true;
      }
    });

    if (disabled) setLoadMoreDatesDisabled(true);
    if (!disabled) increaseNumMonths();
  };

  return (
    <ConfirmationDialog
      dialogTitle={dialogTitle}
      isDialogOpen={isOpen}
      handleCloseDialog={close}
      hasBottomBar={true}
      confirmText={'Save & Continue'}
      padding={0}
      maxWidth={'md'}
      LeftFooterButton={
        isXs ? (
          <Button color="inherit" onClick={clearDates}>
            Clear Dates
          </Button>
        ) : undefined
      }
      handleClickConfirm={() => onSaveDates(selectedDateRange)}
    >

      {!isXs ? (
        <CheckoutDateRangePickerYearMonthSelect
          currYear={currYear}
          currMonth={currMonth}
          lastDateOfRates={lastDateOfRates}
          setCurrMonth={setCurrMonth}
          setCurrYear={setCurrYear}
        />
      ) : null}
      <DatePickerContent>

        <StyledDayPicker
          ref={ref => (calRef.current = ref)}
          className="Selectable"
          initialMonth={month}
          month={month}
          fromMonth={new Date()}
          toMonth={toMonth}
          selectedDays={selectedDays}
          disabledDays={disabledDaysTotal}
          modifiers={modifiers}
          onDayClick={handleDayClick}
          onMonthChange={onMonthChange}
          numberOfMonths={numMonths}
          pagedNavigation={!isXs}
          navbarElement={props => {
            return (
              <DateRangePickerNavbar
                currMonth={currMonth}
                currYear={currYear}
                showNextButton={props.showNextButton}
                showPreviousButton={props.showPreviousButton}
                increaseNumMonths={increaseNumMonths}
                onMonthChange={onMonthChange}
                onNextClick={props.onNextClick}
              />
            );
          }}
          renderDay={(day, modifiers) => {
            return (
              <Fade in>
                <div>
                  <DateRangePickerDay
                    day={day}
                    modifiers={modifiers}
                    selectedDateRange={selectedDateRange}
                    minNights={minNightsDates}
                    ratesData={ratesData}
                  />
                </div>
              </Fade>
            );
          }}
        />


      </DatePickerContent>
      <CheckoutDateRangePickerFooter
        loadMoreDatesDisabled={loadMoreDatesDisabled}
        showNextMonth={showNextMonth}
        onTodayClick={onTodayClick}
        clearDates={clearDates}
      />

    </ConfirmationDialog>
  );
};
